import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
}

export const FileRequirements: React.FC<Props> = ({
  enableDarkTheme = false,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.pageBackground
          : palette.pageBackground,
        padding: '10px',
        marginTop: '10px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          marginBottom: '10px',
          color: palette.textPrimary,
        }}
      >
        {t('file_upload_and_mapping_process')}
      </Typography>
      <Typography
        sx={{
          color: palette.textPrimary,
        }}
      >
        {t('to_ensure_a_smooth_upload_and_mapping_process_please_follow_')}
      </Typography>
      <ul
        style={{
          color: palette.textPrimary,
        }}
      >
        <li>
          {t('ensure_your_csv_file_is_correctly_formatted_and_contains_hea')}
        </li>
        <li>
          {t('check_that_all_required_fields_are_present_in_your_csv_file_')}
        </li>
        <ul>
          <li>
            {t('work_order_number_unique_identifier_for_each_work_order')}
          </li>
          <li>
            {t('asset_id_identifier_for_the_asset_associated_with_the_work_o')}
          </li>
          <li>{t('total_cost_total_cost_associated_with_the_work_order')}</li>
          <li>
            {t('date_of_orders_date_when_the_order_was_placed_format_mm_dd_y')}
          </li>
          <li>
            {t('date_of_completion_actual_completion_date_of_the_order_forma')}
          </li>
          <li>
            {t('category_category_or_classification_of_the_work_order_option')}
          </li>
          <li>
            {t('description_detailed_description_of_the_work_order_optional')}
          </li>
        </ul>
      </ul>
      <Typography
        sx={{
          color: palette.textPrimary,
        }}
      >
        {t('after_selecting_your_file_you_will_map_your_file_apos_s_head')}
      </Typography>
      <Typography
        sx={{
          color: palette.textPrimary,
        }}
      >
        {t('please_ensure_that_your_file_meets_these_requirements_and_fo')}
      </Typography>
    </Box>
  );
};

export const downloadTemplate = () => {
  const csvContent =
    'data:text/csv;charset=utf-8,' + requiredHeaders.join(',') + '\r\n';

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', 'work_orders_template.csv');
  document.body.appendChild(link);

  link.click();
  document.body.removeChild(link);
};

export interface RequiredHeader {
  name: string;
  required: boolean;
}

export const requiredHeaders: RequiredHeader[] = [
  { name: 'work_order_number', required: true },
  { name: 'work_order_status', required: false },
  { name: 'asset_id', required: true },
  { name: 'labour_cost', required: false },
  { name: 'part_cost', required: false },
  { name: 'total_cost', required: true },
  { name: 'date_of_orders', required: true },
  { name: 'date_of_completion', required: false },
  { name: 'category', required: false },
  { name: 'description', required: true },
];

export const SuccessComponent = () => {
  const { t } = useTranslation();
  const [showLoadingBar, setShowLoadingBar] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoadingBar(false); // Hide the loading bar after 4 seconds
    }, 4000);

    return () => {
      clearTimeout(timer);
    }; // Clean up the timer when the component unmounts or rerenders
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: '#e8f5e9', // A light green background or any color you prefer
        padding: '20px',
        margin: '20px 0',
        borderRadius: '4px',
      }}
    >
      <Typography variant="h6" sx={{ color: '#2e7d32' }}>
        {t('upload_successful')}
      </Typography>
      {showLoadingBar && (
        <Box sx={{ marginTop: '10px' }}>
          <LinearProgress />
        </Box>
      )}
    </Box>
  );
};

export const ErrorComponent = () => (
  <Box
    sx={{
      backgroundColor: '#ffebee', // A light red background or any color you prefer
      padding: '20px',
      margin: '20px 0',
      borderRadius: '4px',
    }}
  >
    <Typography variant="h6" sx={{ color: '#d32f2f' }}>
      Upload Failed
    </Typography>
    <Typography>
      There was a problem processing your file. Please check the format and try
      again.
    </Typography>
    <Typography>If the problem persists, please contact the admin.</Typography>
  </Box>
);
